import classNames from 'classnames';
import React, {FC, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {ExtractProps, RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AttachmentsDropzone} from '../../common/attachments/attachmentsDropzone';
import {
  composerBaseScrollableDropzoneClassName,
  stickyComposerToolbarClassName,
} from './composerCommonClassnames';

/*
 * Props.
 */

interface ComposerBaseProps extends ExtractProps<typeof AttachmentsDropzone> {
  header: ReactNode;
  editor: ReactNode;
  toolbar: RendererOf<boolean>;
  isComposerPopout?: boolean;
  shouldDynamicallyGrowComposer?: boolean;
  // Bottom value for toolbar for sticky positioning control
  toolbarBottom?: number;
}

/*
 * Style.
 */

// Minimum size that we should keep between the
// toolbar and the top of the editing area.
const minEditorHeight = 55;

const StyledHeaderDiv = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

interface EditorStyleProps {
  $shouldDynamicallyGrowComposer?: boolean;
}
const StyledEditorDiv = styled.div<EditorStyleProps>`
  flex: 1 1 auto;
  position: relative;

  display: flex;
  flex-direction: column;

  ${(p) => maybeAddOverflowHidden(p)};
`;

const StyledScrollableDropzone = styled(AttachmentsDropzone)`
  flex: 1 1 auto;

  position: relative;
  overflow: auto;
  margin-bottom: -${minEditorHeight}px;

  display: flex;
  flex-direction: column;
`;

interface ToolbarStyleProps {
  $bottom?: number;
}

const StyledToolbarDiv = styled.div<ToolbarStyleProps>`
  flex-shrink: 0;

  position: sticky;
  margin-top: ${minEditorHeight}px;
  ${(p) => css`
    bottom: ${p.$bottom ?? 0}px;
  `}

  display: flex;
  flex-direction: column;
`;

function maybeAddOverflowHidden(props: EditorStyleProps) {
  if (props.$shouldDynamicallyGrowComposer) {
    return '';
  }

  return css`
    overflow: hidden;
  `;
}

/*
 * Component.
 */

export const ComposerBase: FC<ComposerBaseProps> = (props) => {
  const [isFocused, setIsFocused] = useState(false);

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  return (
    <>
      <StyledHeaderDiv onFocus={onFocus} onBlur={onBlur}>
        {props.header}
      </StyledHeaderDiv>
      <StyledEditorDiv
        $shouldDynamicallyGrowComposer={props.shouldDynamicallyGrowComposer}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <StyledScrollableDropzone
          {...props}
          className={classNames(props.className, composerBaseScrollableDropzoneClassName)}
        >
          {props.editor}
        </StyledScrollableDropzone>
        <StyledToolbarDiv className={stickyComposerToolbarClassName} $bottom={props.toolbarBottom}>
          {props.toolbar(isFocused)}
        </StyledToolbarDiv>
      </StyledEditorDiv>
    </>
  );
};
